import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  PrinterOutlined,
  ShoppingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Input,
  Row,
  Select,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { ROUTES } from '@/constants/routes';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useResolvedTheme } from '@/hooks/useResolvedTheme';
import { useNotify } from '@/hooks/useNotify';
import { useGetCustomerQuery } from '@/modules/customers/customersApi';
import {
  useCancelOrderMutation,
  useGetOrderQuery,
  useUpdateOrderStatusMutation,
} from '@/modules/orders/ordersApi';
import type {
  OrderDetail,
  OrderItem,
  OrderShippingInfo,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  UpdateOrderStatusPayload,
} from '@/types/order';
import {
  DISPATCH_BY_OPTIONS,
  DISPATCH_MODE_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
} from '@/types/order';
import { formatOrderMoney, priceOriginLabel } from '@/utils/currency';
import { formatDateTime } from '@/utils/date';
import { getMediaImageUrl } from '@/utils/media';
import {
  getFulfillmentKindColor,
  getFulfillmentKindLabel,
  getOrderStatusLabel,
  getOrderStatusOptions,
  resolveOrderFulfillmentKind,
  type OrderFulfillmentKind,
} from '@/utils/orderFulfillment';
import {
  downloadOrderInvoice,
  downloadOrderReceipt,
  printOrderInvoice,
  printOrderReceipt,
  type OrderInvoiceContext,
} from '@/utils/orderInvoice';
import { resolveMutationResult } from '@/utils/apiResponse';
import {
  confirmOrderCancel,
  confirmOrderStatusUpdate,
  confirmOrderUpdates,
  confirmPaymentStatusUpdate,
} from '@/utils/confirmDialog';
import { ucfirst } from '@/utils/string';
import styles from './OrderDetail.module.scss';

function orderStatusTag(status: OrderStatus, kind: OrderFulfillmentKind = 'print') {
  const colors: Record<OrderStatus, string> = {
    pending: 'gold',
    confirmed: 'blue',
    processing: 'cyan',
    packed: 'purple',
    creating: 'orange',
    shipped: 'geekblue',
    delivered: 'success',
    installed: 'lime',
    closed: 'green',
    cancelled: 'error',
    returned: 'default',
  };

  return <Tag color={colors[status]}>{getOrderStatusLabel(status, kind)}</Tag>;
}

function paymentStatusTag(status: PaymentStatus) {
  const colors: Record<PaymentStatus, string> = {
    pending: 'gold',
    paid: 'success',
    failed: 'error',
    refunded: 'default',
  };

  const label =
    PAYMENT_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? ucfirst(status);
  return <Tag color={colors[status]}>{label}</Tag>;
}

function paymentMethodLabel(method: PaymentMethod): string {
  return PAYMENT_METHOD_OPTIONS.find((option) => option.value === method)?.label ?? ucfirst(method);
}

function fulfillmentKindTag(kind: OrderFulfillmentKind) {
  return <Tag color={getFulfillmentKindColor(kind)}>{getFulfillmentKindLabel(kind)}</Tag>;
}

const CANCELLABLE_STATUSES: OrderStatus[] = [
  'pending',
  'confirmed',
  'processing',
  'packed',
  'creating',
];

/** Rank for fulfillment path. Used so skipped steps (admin jump) still show complete. */
const FULFILLMENT_RANK: Record<string, number> = {
  pending: 0,
  confirmed: 1,
  processing: 2,
  packed: 3,
  creating: 4,
  shipped: 5,
  delivered: 6,
  installed: 7,
  closed: 8,
};

type TimelineStep = {
  label: string;
  date?: string;
  active: boolean;
};

function dispatchModeLabel(mode?: string): string {
  if (!mode) return '—';
  return DISPATCH_MODE_OPTIONS.find((option) => option.value === mode)?.label ?? ucfirst(mode);
}

function buildOrderTimelineSteps(
  order: OrderDetail,
  kind: OrderFulfillmentKind,
): TimelineStep[] {
  if (order.orderStatus === 'cancelled') {
    return [
      { label: 'Order Placed', date: order.placedAt, active: true },
      {
        label: 'Order Accepted',
        date: order.confirmedAt,
        active: Boolean(order.confirmedAt),
      },
      {
        label: 'Cancelled',
        date: order.cancelledAt || order.updatedAt,
        active: true,
      },
    ];
  }

  if (order.orderStatus === 'returned') {
    return [
      { label: 'Order Placed', date: order.placedAt, active: true },
      {
        label: 'Order Accepted',
        date: order.confirmedAt,
        active: Boolean(order.confirmedAt),
      },
      {
        label:
          kind === 'webbased'
            ? 'eFlipBook Details Emailed'
            : kind === 'eflipbook'
              ? 'eFlipBook Emailed'
              : 'Received',
        date: order.deliveredAt,
        active: Boolean(order.deliveredAt),
      },
      {
        label: 'Returned',
        date: order.updatedAt,
        active: true,
      },
    ];
  }

  const rank = FULFILLMENT_RANK[order.orderStatus] ?? 0;
  const reached = (minRank: number) => rank >= minRank;

  const confirmedDate =
    order.confirmedAt ||
    (reached(1) ? order.shippedAt || order.deliveredAt || order.updatedAt : undefined);
  const processingDate = reached(2)
    ? order.orderStatus === 'processing'
      ? order.updatedAt
      : order.shippedAt || order.deliveredAt || order.updatedAt
    : undefined;
  const packedDate = reached(3)
    ? order.orderStatus === 'packed'
      ? order.updatedAt
      : order.shippedAt || order.deliveredAt || order.updatedAt
    : undefined;
  const creatingDate = reached(4)
    ? order.orderStatus === 'creating'
      ? order.updatedAt
      : order.shippedAt || order.deliveredAt || order.updatedAt
    : undefined;
  const shippedDate =
    order.shippedAt || (reached(5) ? order.deliveredAt || order.updatedAt : undefined);
  const deliveredDate = order.deliveredAt || (reached(6) ? order.updatedAt : undefined);
  const installedDate = reached(7) ? order.updatedAt : undefined;
  const closedDate = reached(8) ? order.updatedAt : undefined;

  if (kind === 'webbased') {
    return [
      { label: 'Order Placed', date: order.placedAt, active: true },
      { label: 'Order Accepted', date: confirmedDate, active: Boolean(confirmedDate) || reached(1) },
      { label: 'eFlipBook Under Preparation', date: processingDate, active: reached(2) },
      { label: 'eFlipBook Under Checking', date: packedDate, active: reached(3) },
      { label: 'eFlipBook Under Creation', date: creatingDate, active: reached(4) },
      { label: 'eFlipBook Created', date: shippedDate, active: Boolean(shippedDate) || reached(5) },
      {
        label: 'eFlipBook Details Emailed',
        date: order.details?.eflipSentOn || deliveredDate,
        active: Boolean(deliveredDate) || reached(6),
      },
      { label: 'eFlipBook Viewed First Time', date: installedDate, active: reached(7) },
      { label: 'Confirmed & Closed', date: closedDate, active: reached(8) },
    ];
  }

  if (kind === 'eflipbook') {
    return [
      { label: 'Order Placed', date: order.placedAt, active: true },
      { label: 'Order Accepted', date: confirmedDate, active: Boolean(confirmedDate) || reached(1) },
      { label: 'eFlipBook Under Preparation', date: processingDate, active: reached(2) },
      { label: 'eFlipBook Under Checking', date: packedDate, active: reached(3) },
      { label: 'eFlipBook Under Creation', date: creatingDate, active: reached(4) },
      { label: 'eFlipBook Created', date: shippedDate, active: Boolean(shippedDate) || reached(5) },
      {
        label: 'eFlipBook Emailed',
        date: order.details?.eflipSentOn || deliveredDate,
        active: Boolean(deliveredDate) || reached(6),
      },
      { label: 'eFlipBook Installed', date: installedDate, active: reached(7) },
      { label: 'Confirmed & Closed', date: closedDate, active: reached(8) },
    ];
  }

  return [
    { label: 'Order Placed', date: order.placedAt, active: true },
    { label: 'Order Accepted', date: confirmedDate, active: Boolean(confirmedDate) || reached(1) },
    { label: 'Under Preparation', date: processingDate, active: reached(2) },
    { label: 'Dispatch Awaited', date: packedDate, active: reached(3) },
    { label: 'Dispatched', date: shippedDate, active: Boolean(shippedDate) || reached(5) },
    {
      label: 'Received',
      date: deliveredDate,
      active: Boolean(deliveredDate) || reached(6),
    },
    {
      label: 'Confirmed & Closed',
      date: closedDate,
      active: reached(8),
    },
  ];
}

function formatAddressBlock(info: OrderShippingInfo, type: 'shipping' | 'billing'): string[] {
  if (type === 'shipping') {
    return [
      info.shippingName,
      info.shippingPhone,
      info.shippingAddress1,
      info.shippingAddress2 || '',
      `${info.shippingCity}, ${info.shippingState} ${info.shippingPostalCode}`,
      info.shippingCountry,
    ].filter(Boolean);
  }

  return [
    info.billingName,
    info.billingPhone,
    info.billingAddress1,
    info.billingAddress2 || '',
    `${info.billingCity}, ${info.billingState} ${info.billingPostalCode}`,
    info.billingCountry,
  ].filter(Boolean);
}

function OrderDetailPage() {
  const navigate = useNavigate();
  const notify = useNotify();
  const resolvedTheme = useResolvedTheme();
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading } = useGetOrderQuery(id ?? '', { skip: !id });
  const [cancelOrder, { isLoading: isCancelling }] = useCancelOrderMutation();
  const [updateOrderStatus, { isLoading: isUpdatingStatus }] = useUpdateOrderStatusMutation();
  const [orderStatusDraft, setOrderStatusDraft] = useState<OrderStatus>('pending');
  const [paymentStatusDraft, setPaymentStatusDraft] = useState<PaymentStatus>('pending');
  const [dispatchModeDraft, setDispatchModeDraft] = useState<
    UpdateOrderStatusPayload['dispatchMode'] | undefined
  >(undefined);
  const [dispatchedOnDraft, setDispatchedOnDraft] = useState<Dayjs | null>(null);
  const [dispatchedByDraft, setDispatchedByDraft] = useState('');
  const [docketNumberDraft, setDocketNumberDraft] = useState('');
  const [eflipSentOnDraft, setEflipSentOnDraft] = useState<Dayjs | null>(null);
  const [downloadLimitDraft, setDownloadLimitDraft] = useState('');
  const [downloadUniqueKeyDraft, setDownloadUniqueKeyDraft] = useState('');
  const [eflipUsernameDraft, setEflipUsernameDraft] = useState('');
  const [eflipPasswordDraft, setEflipPasswordDraft] = useState('');
  const [sendEmailDraft, setSendEmailDraft] = useState(false);
  const { data: customer } = useGetCustomerQuery(order?.customerId ?? '', {
    skip: !order?.customerId,
  });
  useGlobalLoader(isLoading);

  const fulfillmentKind = useMemo(
    () => resolveOrderFulfillmentKind(order?.items),
    [order?.items],
  );
  const statusOptions = useMemo(
    () => getOrderStatusOptions(fulfillmentKind),
    [fulfillmentKind],
  );

  useEffect(() => {
    if (!order) return;
    setOrderStatusDraft(order.orderStatus);
    setPaymentStatusDraft(order.paymentStatus);
    setDispatchModeDraft(
      (order.details?.dispatchMode as UpdateOrderStatusPayload['dispatchMode'] | undefined) ||
        undefined,
    );
    setDispatchedOnDraft(order.shippedAt ? dayjs(order.shippedAt) : null);
    setDispatchedByDraft(order.details?.courierName || '');
    setDocketNumberDraft(order.details?.trackingNumber || '');
    setEflipSentOnDraft(order.details?.eflipSentOn ? dayjs(order.details.eflipSentOn) : null);
    setDownloadLimitDraft(order.details?.downloadLimit || '');
    setDownloadUniqueKeyDraft(order.details?.downloadUniqueKey || '');
    setEflipUsernameDraft(order.details?.eflipUsername || '');
    setEflipPasswordDraft(order.details?.eflipPassword || '');
    setSendEmailDraft(false);
  }, [order]);

  const details = order?.details;
  const showDispatchForm = fulfillmentKind === 'print' && orderStatusDraft === 'shipped';
  /** eFlipBook: form opens on "eFlipBook Emailed". */
  const showEflipForm = fulfillmentKind === 'eflipbook' && orderStatusDraft === 'delivered';
  /** Web Based: form opens on "eFlipBook Created"; submit advances to Details Emailed. */
  const showWebbasedForm = fulfillmentKind === 'webbased' && orderStatusDraft === 'shipped';

  const dispatchFieldsDirty = Boolean(
    order &&
      showDispatchForm &&
      ((dispatchModeDraft || '') !== (order.details?.dispatchMode || '') ||
        (dispatchedByDraft || '') !== (order.details?.courierName || '') ||
        (docketNumberDraft || '') !== (order.details?.trackingNumber || '') ||
        (dispatchedOnDraft?.format('YYYY-MM-DD') || '') !==
          (order.shippedAt ? dayjs(order.shippedAt).format('YYYY-MM-DD') : '')),
  );

  const eflipFieldsDirty = Boolean(
    order &&
      showEflipForm &&
      ((downloadLimitDraft || '') !== (order.details?.downloadLimit || '') ||
        (downloadUniqueKeyDraft || '') !== (order.details?.downloadUniqueKey || '') ||
        (eflipSentOnDraft?.format('YYYY-MM-DD') || '') !==
          (order.details?.eflipSentOn
            ? dayjs(order.details.eflipSentOn).format('YYYY-MM-DD')
            : '')),
  );

  const webbasedFieldsDirty = Boolean(
    order &&
      showWebbasedForm &&
      ((eflipUsernameDraft || '') !== (order.details?.eflipUsername || '') ||
        (eflipPasswordDraft || '') !== (order.details?.eflipPassword || '') ||
        (eflipSentOnDraft?.format('YYYY-MM-DD') || '') !==
          (order.details?.eflipSentOn
            ? dayjs(order.details.eflipSentOn).format('YYYY-MM-DD')
            : '')),
  );
  const customerName = details?.customerName || order?.customerName || customer?.name || '—';
  const customerPhone = details?.customerPhone || order?.customerPhone;
  const customerEmail = details?.customerEmail || order?.customerEmail || customer?.email;

  const invoiceContext = useMemo<OrderInvoiceContext>(
    () => ({
      customerName,
      customerPhone,
      customerEmail,
    }),
    [customerEmail, customerName, customerPhone],
  );

  const handlePrintInvoice = useCallback(() => {
    if (!order) {
      notify.error('Order is not loaded yet.');
      return;
    }
    const opened = printOrderInvoice(order, invoiceContext);
    if (!opened) {
      notify.error('Pop-up blocked. Allow pop-ups for this site to print.');
    }
  }, [invoiceContext, notify, order]);

  const handleDownloadInvoice = useCallback(async () => {
    if (!order) {
      notify.error('Order is not loaded yet.');
      return;
    }
    try {
      await downloadOrderInvoice(order, invoiceContext);
      notify.success('Invoice PDF downloaded.');
    } catch {
      notify.error('Could not generate invoice PDF.');
    }
  }, [invoiceContext, notify, order]);

  const handlePrintReceipt = useCallback(() => {
    if (!order) {
      notify.error('Order is not loaded yet.');
      return;
    }
    const opened = printOrderReceipt(order, invoiceContext);
    if (!opened) {
      notify.error('Pop-up blocked. Allow pop-ups for this site to print.');
    }
  }, [invoiceContext, notify, order]);

  const handleDownloadReceipt = useCallback(async () => {
    if (!order) {
      notify.error('Order is not loaded yet.');
      return;
    }
    try {
      await downloadOrderReceipt(order, invoiceContext);
      notify.success('Receipt PDF downloaded.');
    } catch {
      notify.error('Could not generate receipt PDF.');
    }
  }, [invoiceContext, notify, order]);

  const canCancelOrder = Boolean(order && CANCELLABLE_STATUSES.includes(order.orderStatus));

  const hasStatusChanges = Boolean(
    order &&
      (orderStatusDraft !== order.orderStatus ||
        paymentStatusDraft !== order.paymentStatus ||
        dispatchFieldsDirty ||
        eflipFieldsDirty ||
        webbasedFieldsDirty),
  );

  const orderStatusChanging = Boolean(order && orderStatusDraft !== order.orderStatus);

  const paymentStatusChanging = Boolean(order && paymentStatusDraft !== order.paymentStatus);

  const performStatusUpdate = useCallback(async () => {
    if (!order || !hasStatusChanges) return;

    if (showDispatchForm) {
      if (!dispatchModeDraft) {
        notify.error('Please select Dispatched Mode.');
        return;
      }
      if (!dispatchedOnDraft) {
        notify.error('Please select Dispatched On date.');
        return;
      }
      if (!dispatchedByDraft.trim()) {
        notify.error('Please enter Dispatched By.');
        return;
      }
      if (!docketNumberDraft.trim()) {
        notify.error('Please enter Docket Number.');
        return;
      }
    }

    if (showEflipForm) {
      if (!eflipSentOnDraft) {
        notify.error('Please select Sent On date.');
        return;
      }
      if (!downloadLimitDraft.trim()) {
        notify.error('Please enter Download Limit.');
        return;
      }
      if (!downloadUniqueKeyDraft.trim()) {
        notify.error('Please enter Download Unique Key.');
        return;
      }
    }

    if (showWebbasedForm) {
      if (!eflipSentOnDraft) {
        notify.error('Please select Sent On date.');
        return;
      }
      if (!eflipUsernameDraft.trim()) {
        notify.error('Please enter Username.');
        return;
      }
      if (!eflipPasswordDraft.trim()) {
        notify.error('Please enter Password.');
        return;
      }
    }

    const body: UpdateOrderStatusPayload = {};
    if (orderStatusDraft !== order.orderStatus) body.orderStatus = orderStatusDraft;
    if (paymentStatusDraft !== order.paymentStatus) body.paymentStatus = paymentStatusDraft;

    if (showDispatchForm) {
      body.dispatchMode = dispatchModeDraft;
      body.courierName = dispatchedByDraft.trim();
      body.trackingNumber = docketNumberDraft.trim();
      body.shippedAt = dispatchedOnDraft?.startOf('day').toISOString();
    }

    if (showEflipForm) {
      body.eflipSentOn = eflipSentOnDraft?.startOf('day').toISOString();
      body.downloadLimit = downloadLimitDraft.trim();
      body.downloadUniqueKey = downloadUniqueKeyDraft.trim();
    }

    if (showWebbasedForm) {
      body.eflipSentOn = eflipSentOnDraft?.startOf('day').toISOString();
      body.eflipUsername = eflipUsernameDraft.trim();
      body.eflipPassword = eflipPasswordDraft.trim();
      // Submitting credentials from Created advances status to Details Emailed.
      body.orderStatus = 'delivered';
      if (!order.shippedAt) {
        body.shippedAt = new Date().toISOString();
      }
    }

    if (showDispatchForm || showEflipForm || showWebbasedForm) {
      body.sendEmail = sendEmailDraft;
    }

    const result = await resolveMutationResult(updateOrderStatus({ id: order.id, body }));
    if (result.ok) {
      notify.success(result.message ?? 'Order status updated.');
      setSendEmailDraft(false);
      return;
    }
    notify.error(result.message);
  }, [
    dispatchFieldsDirty,
    dispatchModeDraft,
    dispatchedByDraft,
    dispatchedOnDraft,
    docketNumberDraft,
    downloadLimitDraft,
    downloadUniqueKeyDraft,
    eflipFieldsDirty,
    eflipPasswordDraft,
    eflipSentOnDraft,
    eflipUsernameDraft,
    hasStatusChanges,
    notify,
    order,
    orderStatusDraft,
    paymentStatusDraft,
    sendEmailDraft,
    showDispatchForm,
    showEflipForm,
    showWebbasedForm,
    updateOrderStatus,
    webbasedFieldsDirty,
  ]);

  const handleUpdateStatus = useCallback(async () => {
    if (!order || !hasStatusChanges) return;

    // Detail-only edits skip the status-transition confirm dialog.
    if (
      !orderStatusChanging &&
      !paymentStatusChanging &&
      (dispatchFieldsDirty || eflipFieldsDirty || webbasedFieldsDirty)
    ) {
      await performStatusUpdate();
      return;
    }

    const orderPreviousLabel = getOrderStatusLabel(order.orderStatus, fulfillmentKind);
    const orderNextLabel = showWebbasedForm
      ? getOrderStatusLabel('delivered', fulfillmentKind)
      : getOrderStatusLabel(orderStatusDraft, fulfillmentKind);
    const paymentPreviousLabel =
      PAYMENT_STATUS_OPTIONS.find((option) => option.value === order.paymentStatus)?.label ??
      ucfirst(order.paymentStatus);
    const paymentNextLabel =
      PAYMENT_STATUS_OPTIONS.find((option) => option.value === paymentStatusDraft)?.label ??
      ucfirst(paymentStatusDraft);
    const emailTarget = customerEmail?.trim();

    let confirmed = true;

    if (orderStatusChanging && paymentStatusChanging) {
      confirmed = await confirmOrderUpdates(
        {
          orderStatus: {
            fieldLabel: 'Order status',
            previousLabel: orderPreviousLabel,
            nextLabel: orderNextLabel,
          },
          paymentStatus: {
            fieldLabel: 'Payment status',
            previousLabel: paymentPreviousLabel,
            nextLabel: paymentNextLabel,
          },
          emailTarget: emailTarget || undefined,
        },
        resolvedTheme,
      );
    } else if (orderStatusChanging) {
      confirmed = await confirmOrderStatusUpdate(
        {
          previousLabel: orderPreviousLabel,
          nextLabel: orderNextLabel,
          emailTarget: emailTarget || undefined,
        },
        resolvedTheme,
      );
    } else if (paymentStatusChanging) {
      confirmed = await confirmPaymentStatusUpdate(
        {
          previousLabel: paymentPreviousLabel,
          nextLabel: paymentNextLabel,
        },
        resolvedTheme,
      );
    }

    if (confirmed) {
      await performStatusUpdate();
    }
  }, [
    customerEmail,
    dispatchFieldsDirty,
    eflipFieldsDirty,
    fulfillmentKind,
    hasStatusChanges,
    order,
    orderStatusChanging,
    orderStatusDraft,
    paymentStatusChanging,
    paymentStatusDraft,
    performStatusUpdate,
    resolvedTheme,
    showWebbasedForm,
    webbasedFieldsDirty,
  ]);

  const handleCancelOrder = useCallback(async () => {
    if (!order) return;

    const confirmed = await confirmOrderCancel(
      { emailTarget: customerEmail?.trim() || undefined },
      resolvedTheme,
    );

    if (!confirmed) return;

    const result = await resolveMutationResult(cancelOrder(order.id));
    if (result.ok) {
      notify.success(result.message ?? 'Order cancelled.');
      return;
    }
    notify.error(result.message);
  }, [cancelOrder, customerEmail, notify, order, resolvedTheme]);

  const itemColumns: ColumnsType<OrderItem> = useMemo(
    () => [
      {
        title: 'Product',
        key: 'product',
        render: (_value, item) => {
          const imageUrl = getMediaImageUrl(item.image);
          return (
            <div className={styles.productCell}>
              <div className={styles.productImage}>
                {imageUrl ? (
                  <PreviewImage src={imageUrl} alt={item.productName} size={48} fill />
                ) : (
                  <div className={styles.productPlaceholder}>
                    <ShoppingOutlined />
                  </div>
                )}
              </div>
              <div className={styles.productInfo}>
                <Link to={`/products/${item.productId}/view`} className={styles.productName}>
                  {item.productName}
                </Link>
                <span className={styles.productSku}>{item.sku}</span>
                {item.attributes.length > 0 ? (
                  <div className={styles.attributes}>
                    {item.attributes.map((attribute) => (
                      <Tag key={`${attribute.name}-${attribute.value}`}>
                        {attribute.name}: {attribute.value}
                      </Tag>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        title: 'Qty',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 72,
        align: 'center',
      },
      {
        title: 'MRP',
        dataIndex: 'mrp',
        key: 'mrp',
        render: (value: number) => formatOrderMoney(value, order?.priceOrigin),
      },
      {
        title: 'Price',
        dataIndex: 'salePrice',
        key: 'salePrice',
        render: (value: number) => formatOrderMoney(value, order?.priceOrigin),
      },
      {
        title: 'Discount',
        dataIndex: 'discount',
        key: 'discount',
        render: (value: number) =>
          value > 0 ? `-${formatOrderMoney(value, order?.priceOrigin)}` : '—',
      },
      {
        title: 'Tax',
        dataIndex: 'tax',
        key: 'tax',
        render: (value: number) => formatOrderMoney(value, order?.priceOrigin),
      },
      {
        title: 'Line total',
        dataIndex: 'total',
        key: 'total',
        render: (value: number) => <strong>{formatOrderMoney(value, order?.priceOrigin)}</strong>,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: OrderItem['status']) => {
          if (status === 'cancelled') return <Tag color="error">Cancelled</Tag>;
          if (status === 'returned') return <Tag>Returned</Tag>;
          return <Tag color="success">Active</Tag>;
        },
      },
    ],
    [order?.priceOrigin],
  );

  const timelineSteps = useMemo(() => {
    if (!order) return [];
    return buildOrderTimelineSteps(order, fulfillmentKind);
  }, [fulfillmentKind, order]);

  const collapseItems = useMemo(() => {
    if (!order) return [];

    return [
      {
        key: 'items',
        label: `Order items (${order.items.length})`,
        children: (
          <Table<OrderItem>
            className={styles.itemsTable}
            rowKey="id"
            size="small"
            columns={itemColumns}
            dataSource={order.items}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No items found for this order.' }}
          />
        ),
      },
      {
        key: 'addresses',
        label: 'Shipping & billing',
        children: (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <div className={styles.addressPanel}>
                <Typography.Title level={5} className={styles.panelTitle}>
                  <EnvironmentOutlined /> Shipping address
                </Typography.Title>
                {details ? (
                  <div className={styles.addressBlock}>
                    {formatAddressBlock(details, 'shipping').map((line, index) => (
                      <span
                        key={`${line}-${index}`}
                        className={index === 0 ? styles.addressName : undefined}
                      >
                        {line}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className={styles.emptyValue}>—</span>
                )}
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div className={styles.addressPanel}>
                <Typography.Title level={5} className={styles.panelTitle}>
                  <EnvironmentOutlined /> Billing address
                </Typography.Title>
                {details ? (
                  <div className={styles.addressBlock}>
                    {formatAddressBlock(details, 'billing').map((line, index) => (
                      <span
                        key={`${line}-${index}`}
                        className={index === 0 ? styles.addressName : undefined}
                      >
                        {line}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className={styles.emptyValue}>—</span>
                )}
              </div>
            </Col>
          </Row>
        ),
      },
      {
        key: 'fulfillment',
        label: 'Fulfillment & tracking',
        children: (
          <div className={styles.metaGrid}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Dispatched mode</span>
              <span className={styles.metaValue}>
                {dispatchModeLabel(details?.dispatchMode)}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Dispatched on</span>
              <span className={styles.metaValue}>
                {order.shippedAt ? formatDateTime(order.shippedAt) : '—'}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Dispatched by</span>
              <span className={styles.metaValue}>{details?.courierName || '—'}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Docket number</span>
              <span className={styles.metaValue}>{details?.trackingNumber || '—'}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Sent on (eFlipBook)</span>
              <span className={styles.metaValue}>
                {details?.eflipSentOn ? formatDateTime(details.eflipSentOn) : '—'}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Download limit</span>
              <span className={styles.metaValue}>{details?.downloadLimit || '—'}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Download unique key</span>
              <span className={styles.metaValue}>{details?.downloadUniqueKey || '—'}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Username (Web Based)</span>
              <span className={styles.metaValue}>{details?.eflipUsername || '—'}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Password (Web Based)</span>
              <span className={styles.metaValue}>{details?.eflipPassword || '—'}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Tracking URL</span>
              <span className={styles.metaValue}>
                {details?.trackingUrl ? (
                  <a
                    href={details.trackingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.metaLink}
                  >
                    Open tracking
                  </a>
                ) : (
                  '—'
                )}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Delivery instructions</span>
              <span className={styles.metaValue}>{details?.deliveryInstructions || '—'}</span>
            </div>
          </div>
        ),
      },
      {
        key: 'customer',
        label: 'Customer information',
        children: (
          <div className={styles.metaGrid}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Name</span>
              <span className={styles.metaValue}>{customerName}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Phone</span>
              <span className={styles.metaValue}>{customerPhone || '—'}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Email</span>
              <span className={styles.metaValue}>{customerEmail || '—'}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Customer profile</span>
              <span className={styles.metaValue}>
                <Link to={`/customers/${order.customerId}`} className={styles.metaLink}>
                  View customer
                </Link>
              </span>
            </div>
          </div>
        ),
      },
      {
        key: 'payment',
        label: 'Payment summary',
        children: (
          <div className={styles.totalsList}>
            <div className={styles.totalRow}>
              <span>Price origin</span>
              <span>{priceOriginLabel(order.priceOrigin)}</span>
            </div>
            <div className={styles.totalRow}>
              <span>Subtotal</span>
              <span>{formatOrderMoney(order.subtotal, order.priceOrigin)}</span>
            </div>
            <div className={styles.totalRow}>
              <span>Discount</span>
              <span className={styles.discountValue}>
                -{formatOrderMoney(order.discountAmount, order.priceOrigin)}
              </span>
            </div>
            <div className={styles.totalRow}>
              <span>Coupon</span>
              <span>{order.couponCode || '—'}</span>
            </div>
            <div className={styles.totalRow}>
              <span>Shipping</span>
              <span>{formatOrderMoney(order.shippingCharge, order.priceOrigin)}</span>
            </div>
            <div className={styles.totalRow}>
              <span>Tax</span>
              <span>{formatOrderMoney(order.taxAmount, order.priceOrigin)}</span>
            </div>
            <div className={`${styles.totalRow} ${styles.totalRowGrand}`}>
              <span>Grand total</span>
              <span>{formatOrderMoney(order.grandTotal, order.priceOrigin)}</span>
            </div>
            <div className={styles.totalRow}>
              <span>Payment method</span>
              <span>{paymentMethodLabel(order.paymentMethod)}</span>
            </div>
            <div className={styles.totalRow}>
              <span>Payment status</span>
              <span>{paymentStatusTag(order.paymentStatus)}</span>
            </div>
          </div>
        ),
      },
      {
        key: 'timeline',
        label: 'Order timeline',
        children: (
          <div className={styles.timeline}>
            {timelineSteps.map((step) => (
              <div key={step.label} className={styles.timelineItem}>
                <span
                  className={`${styles.timelineDot} ${step.active ? '' : styles.timelineDotMuted}`}
                />
                <div className={styles.timelineContent}>
                  <span className={styles.timelineLabel}>{step.label}</span>
                  <span className={styles.timelineDate}>
                    {step.date ? formatDateTime(step.date) : 'Not yet'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ),
      },
      {
        key: 'meta',
        label: 'Order metadata',
        children: (
          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 2, lg: 3 }}
            className={styles.descriptions}
          >
            <Descriptions.Item label="Order ID">{order.id}</Descriptions.Item>
            <Descriptions.Item label="Order number">{order.orderNumber}</Descriptions.Item>
            <Descriptions.Item label="Customer ID">{order.customerId}</Descriptions.Item>
            <Descriptions.Item label="Total items">{order.totalItems}</Descriptions.Item>
            <Descriptions.Item label="Total quantity">{order.totalQuantity}</Descriptions.Item>
            <Descriptions.Item label="Delivery status">
              {orderStatusTag(order.orderStatus, fulfillmentKind)}
            </Descriptions.Item>
            <Descriptions.Item label="Created">{formatDateTime(order.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Last updated">
              {formatDateTime(order.updatedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Order note" span={3}>
              {order.note?.trim() ? order.note : '—'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
    ];
  }, [customerEmail, customerName, customerPhone, details, fulfillmentKind, itemColumns, order, timelineSteps]);

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <PageHeader
          actions={
            <div className={styles.actionBar}>
              <Button
                icon={<ArrowLeftOutlined />}
                className={styles.headerBtn}
                onClick={() => navigate(ROUTES.ORDERS)}
              >
                Back to orders
              </Button>
              <Button
                icon={<DownloadOutlined />}
                className={styles.invoiceBtn}
                onClick={handleDownloadInvoice}
                disabled={!order}
              >
                Download invoice PDF
              </Button>
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                className={styles.printBtn}
                onClick={handlePrintInvoice}
                disabled={!order}
              >
                Print invoice
              </Button>
              <Button
                icon={<DownloadOutlined />}
                className={styles.invoiceBtn}
                onClick={handleDownloadReceipt}
                disabled={!order}
              >
                Download receipt PDF
              </Button>
              <Button
                icon={<PrinterOutlined />}
                className={styles.printBtn}
                onClick={handlePrintReceipt}
                disabled={!order}
              >
                Print receipt
              </Button>
            </div>
          }
        />
      </div>

      <Card loading={isLoading} className={styles.summaryCard}>
        <div className={styles.orderHero}>
          <span className={styles.heroGlow} aria-hidden />
          <div className={styles.heroContent}>
            <Typography.Title level={3} className={styles.orderNumber}>
              {order?.orderNumber ?? '—'}
            </Typography.Title>
            <Typography.Paragraph className={styles.placedAt}>
              <CalendarOutlined /> Placed {order ? formatDateTime(order.placedAt) : '—'}
            </Typography.Paragraph>
            {order ? (
              <Link to={`/customers/${order.customerId}`} className={styles.customerLink}>
                <UserOutlined />
                {customerName}
                {customerPhone ? ` · ${customerPhone}` : ''}
                {customerEmail ? ` · ${customerEmail}` : ''}
              </Link>
            ) : null}
          </div>

          {order ? (
            <div className={styles.statusGroup}>
              <div className={styles.statusGroupItem}>
                <span className={styles.statusGroupLabel}>Format</span>
                {fulfillmentKindTag(fulfillmentKind)}
              </div>
              <div className={styles.statusGroupItem}>
                <span className={styles.statusGroupLabel}>Delivery</span>
                {orderStatusTag(order.orderStatus, fulfillmentKind)}
              </div>
              <div className={styles.statusGroupItem}>
                <span className={styles.statusGroupLabel}>Payment</span>
                {paymentStatusTag(order.paymentStatus)}
              </div>
              <div className={styles.statusGroupItem}>
                <span className={styles.statusGroupLabel}>Method</span>
                <Tag>{paymentMethodLabel(order.paymentMethod)}</Tag>
              </div>
            </div>
          ) : null}
        </div>

        <Row gutter={[16, 16]} className={styles.statsRow}>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} bordered={false}>
              <span className={styles.statLabel}>Items</span>
              <span className={styles.statValue}>
                {order ? `${order.totalItems} · ${order.totalQuantity} qty` : '—'}
              </span>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} bordered={false}>
              <span className={styles.statLabel}>Subtotal</span>
              <span className={styles.statValue}>
                {order ? formatOrderMoney(order.subtotal, order.priceOrigin) : '—'}
              </span>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} bordered={false}>
              <span className={styles.statLabel}>Grand total</span>
              <span className={`${styles.statValue} ${styles.statValueAccent}`}>
                {order ? formatOrderMoney(order.grandTotal, order.priceOrigin) : '—'}
              </span>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} bordered={false}>
              <span className={styles.statLabel}>Coupon</span>
              <span className={styles.statValue}>{order?.couponCode || '—'}</span>
            </Card>
          </Col>
        </Row>

        {order ? (
          <div
            className={`${styles.orderActionsPanel} ${
              order.orderStatus === 'cancelled' ? styles.orderActionsPanelCancelled : ''
            }`}
          >
            <div className={styles.orderActionsInfo}>
              <span className={styles.orderActionsTitle}>Manage order</span>
              <span className={styles.orderActionsHint}>
                Update order and payment status. Changes ask for confirmation before saving. Order
                status updates email the customer. Cancelled or returned statuses restore inventory;
                reactivating an order deducts stock again.
                {fulfillmentKind === 'webbased'
                  ? ' This order is Web Based — select eFlipBook Created to enter username/password; Update status moves it to Details Emailed.'
                  : fulfillmentKind === 'eflipbook'
                    ? ' This order is eFlipBook — select eFlipBook Emailed to enter Sent On / download limit / unique key.'
                    : ' This order is Print fulfillment — print book-status steps are shown.'}
              </span>
            </div>

            <div className={styles.statusManager}>
              <div className={styles.statusField}>
                <span className={styles.statusFieldLabel}>Delivery status</span>
                <Select<OrderStatus>
                  size="middle"
                  value={orderStatusDraft}
                  onChange={(value) => {
                    setOrderStatusDraft(value);
                    if (fulfillmentKind === 'print' && value === 'shipped' && !dispatchedOnDraft) {
                      setDispatchedOnDraft(dayjs());
                    }
                    if (fulfillmentKind === 'eflipbook' && value === 'delivered' && !eflipSentOnDraft) {
                      setEflipSentOnDraft(dayjs());
                    }
                    if (fulfillmentKind === 'webbased' && value === 'shipped' && !eflipSentOnDraft) {
                      setEflipSentOnDraft(dayjs());
                    }
                  }}
                  options={statusOptions}
                  className={styles.statusSelect}
                />
              </div>
              <div className={styles.statusField}>
                <span className={styles.statusFieldLabel}>Payment status</span>
                <Select<PaymentStatus>
                  size="middle"
                  value={paymentStatusDraft}
                  onChange={setPaymentStatusDraft}
                  options={PAYMENT_STATUS_OPTIONS}
                  className={styles.statusSelect}
                />
              </div>
              <div className={styles.statusActions}>
                <Button
                  type="primary"
                  className={styles.updateStatusBtn}
                  onClick={handleUpdateStatus}
                  loading={isUpdatingStatus}
                  disabled={!hasStatusChanges}
                >
                  Update status
                </Button>
                {canCancelOrder ? (
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    className={styles.cancelOrderBtn}
                    onClick={handleCancelOrder}
                    loading={isCancelling}
                  >
                    Quick cancel
                  </Button>
                ) : null}
              </div>
            </div>

            {showDispatchForm ? (
              <div className={styles.dispatchForm}>
                <span className={styles.dispatchFormTitle}>Dispatch details</span>
                <div className={styles.dispatchFormGrid}>
                  <div className={styles.statusField}>
                    <span className={styles.statusFieldLabel}>Dispatched mode</span>
                    <Select
                      size="middle"
                      placeholder="By Hand, Courier, Speed Post"
                      value={dispatchModeDraft}
                      onChange={setDispatchModeDraft}
                      options={DISPATCH_MODE_OPTIONS}
                      className={styles.statusSelect}
                      allowClear
                    />
                  </div>
                  <div className={styles.statusField}>
                    <span className={styles.statusFieldLabel}>Dispatched on</span>
                    <DatePicker
                      size="middle"
                      value={dispatchedOnDraft}
                      onChange={(value) => setDispatchedOnDraft(value)}
                      className={styles.statusSelect}
                      format="DD MMM YYYY"
                    />
                  </div>
                  <div className={styles.statusField}>
                    <span className={styles.statusFieldLabel}>Dispatched by</span>
                    <Select
                      size="middle"
                      placeholder="DTDC, DHL, Government Speed Post"
                      value={dispatchedByDraft || undefined}
                      onChange={setDispatchedByDraft}
                      options={DISPATCH_BY_OPTIONS.map((name) => ({ label: name, value: name }))}
                      className={styles.statusSelect}
                      showSearch
                      allowClear
                    />
                  </div>
                  <div className={styles.statusField}>
                    <span className={styles.statusFieldLabel}>Docket number</span>
                    <Input
                      size="middle"
                      value={docketNumberDraft}
                      onChange={(event) => setDocketNumberDraft(event.target.value)}
                      placeholder="Enter docket / tracking number"
                    />
                  </div>
                </div>
                <Checkbox
                  checked={sendEmailDraft}
                  onChange={(event) => setSendEmailDraft(event.target.checked)}
                >
                  Send Email
                </Checkbox>
              </div>
            ) : null}

            {showEflipForm ? (
              <div className={styles.dispatchForm}>
                <span className={styles.dispatchFormTitle}>eFlipBook Emailed details</span>
                <div className={styles.dispatchFormGrid}>
                  <div className={styles.statusField}>
                    <span className={styles.statusFieldLabel}>Sent on</span>
                    <DatePicker
                      size="middle"
                      value={eflipSentOnDraft}
                      onChange={(value) => setEflipSentOnDraft(value)}
                      className={styles.statusSelect}
                      format="DD MMM YYYY"
                    />
                  </div>
                  <div className={styles.statusField}>
                    <span className={styles.statusFieldLabel}>Download limit</span>
                    <Input
                      size="middle"
                      value={downloadLimitDraft}
                      onChange={(event) => setDownloadLimitDraft(event.target.value)}
                      placeholder="e.g. 3"
                    />
                  </div>
                  <div className={styles.statusField}>
                    <span className={styles.statusFieldLabel}>Download unique key</span>
                    <Input
                      size="middle"
                      value={downloadUniqueKeyDraft}
                      onChange={(event) => setDownloadUniqueKeyDraft(event.target.value)}
                      placeholder="Enter unique key"
                    />
                  </div>
                </div>
                <Checkbox
                  checked={sendEmailDraft}
                  onChange={(event) => setSendEmailDraft(event.target.checked)}
                >
                  Send Email
                </Checkbox>
              </div>
            ) : null}

            {showWebbasedForm ? (
              <div className={styles.dispatchForm}>
                <span className={styles.dispatchFormTitle}>
                  eFlipBook Created — enter details (saves as Details Emailed)
                </span>
                <div className={styles.dispatchFormGrid}>
                  <div className={styles.statusField}>
                    <span className={styles.statusFieldLabel}>Sent on</span>
                    <DatePicker
                      size="middle"
                      value={eflipSentOnDraft}
                      onChange={(value) => setEflipSentOnDraft(value)}
                      className={styles.statusSelect}
                      format="DD MMM YYYY"
                    />
                  </div>
                  <div className={styles.statusField}>
                    <span className={styles.statusFieldLabel}>Username</span>
                    <Input
                      size="middle"
                      value={eflipUsernameDraft}
                      onChange={(event) => setEflipUsernameDraft(event.target.value)}
                      placeholder="Enter username"
                    />
                  </div>
                  <div className={styles.statusField}>
                    <span className={styles.statusFieldLabel}>Password</span>
                    <Input.Password
                      size="middle"
                      value={eflipPasswordDraft}
                      onChange={(event) => setEflipPasswordDraft(event.target.value)}
                      placeholder="Enter password"
                    />
                  </div>
                </div>
                <Checkbox
                  checked={sendEmailDraft}
                  onChange={(event) => setSendEmailDraft(event.target.checked)}
                >
                  Send Email
                </Checkbox>
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>

      <Card className={styles.sectionsCard} loading={isLoading}>
        <div className={styles.sectionsHeader}>
          <InfoCircleOutlined />
          <span>Complete order information</span>
        </div>
        <Collapse bordered={false} className={styles.collapse} items={collapseItems} />
      </Card>
    </div>
  );
}

export default OrderDetailPage;
